import { useEffect, useState } from "react";
import Language from "../lib/Language";
import API from "../lib/TimeEditAPI";
import _ from "underscore";
import { Button } from "@timeedit/ui-components/components/Button/Button";
import { Typography } from "@timeedit/ui-components/components/Typography/Typography";
import Icon from "@timeedit/ui-components/components/Icon/Icon";
import { theme, Checkbox } from "@timeedit/ui-components/antd";
import Modal from "@timeedit/ui-components/antd/modal/index";
import {
    DeleteOutlined,
    EditOutlined,
    LogoutOutlined,
} from "@timeedit/ui-components/components/Icon/Icons/index";
import NamePromptModal from "./coretopbar/NamePromptModal";
import { TComponent } from "../types/utils";
import { TReservationLayer } from "../types/models/TReservationLayer";

type TMassChangeBarProps = {
    isModify: boolean;
    title: string;
    style: React.CSSProperties;
    isWorking?: boolean;
    progress: number;
    performMassChange: (
        ignoreUnchangedReservations?: boolean,
        allowDoubleReservations?: boolean
    ) => void;
    activeLayer: number;
    layers: TReservationLayer[];
    onLeaveDraft: () => void;
    onDeleteDraft: (layerId: number) => void;
    reloadLayers: (callback?: () => void) => void;
};

const TeuiMassChangeBar: TComponent<TMassChangeBarProps> = (props) => {
    const token = theme.useToken();

    const [ignoreUnchangedReservations, setIgnoreUnchangedReservations] = useState(false);
    const [allowMassChangeDoubleReservations, setAllowMassChangeDoubleReservations] =
        useState(false);
    const [renameModalOpen, setRenameModalOpen] = useState(false);
    const [modal, confirmModalContextHolder] = Modal.useModal();

    useEffect(() => {
        if (props.isModify) {
            API.getPreferences("ignoreUnchangedReservations", undefined, undefined, (ignore) => {
                if (ignore === true) {
                    setIgnoreUnchangedReservations(true);
                } else {
                    setIgnoreUnchangedReservations(false);
                }
            });
        }
    }, []);

    const activeLayerData = props.layers.find((lr) => lr.id === props.activeLayer);

    const handleRename = (newName: string) => {
        if (activeLayerData) {
            API.renameReservationLayer(
                activeLayerData.id,
                newName,
                activeLayerData.description,
                () => {
                    props.reloadLayers();
                }
            );
        }
        setRenameModalOpen(false);
    };

    const handleDelete = () => {
        if (!activeLayerData) {
            return;
        }
        modal.confirm({
            icon: <Icon alt={Language.get("nc_draft_confirm_cancel_title")} id="Warning" />,
            title: Language.get("nc_draft_confirm_cancel_title"),
            content: activeLayerData.name,
            okText: Language.get("nc_draft_confirm_remove"),
            cancelText: Language.get("nc_draft_dont_remove"),
            onOk: () => {
                props.onDeleteDraft(activeLayerData.id);
            },
        });
    };

    const loadIndicator = props.isWorking ? (
        <Typography.Text>{Language.get("nc_mass_change_working", props.progress)}</Typography.Text>
    ) : null;

    const ignoreUnchanged = props.isModify ? (
        <Checkbox
            type="checkbox"
            onChange={(event) => {
                API.setPreferences("ignoreUnchangedReservations", [event.target.checked], _.noop);
                setIgnoreUnchangedReservations(event.target.checked);
            }}
            checked={ignoreUnchangedReservations}
        >
            {Language.get("nc_mass_change_ignore_unchanged")}
        </Checkbox>
    ) : null;

    const buttons = (
        <>
            <Button
                type="primary"
                onClick={() => {
                    props.performMassChange(
                        ignoreUnchangedReservations,
                        allowMassChangeDoubleReservations
                    );
                }}
            >
                {Language.get("nc_mass_change_perform")}
            </Button>
            {ignoreUnchanged}
            <Checkbox
                onChange={(event) => {
                    setAllowMassChangeDoubleReservations(event.target.checked);
                }}
                checked={allowMassChangeDoubleReservations}
            >
                {Language.get("nc_mass_change_allow_double_res")}
            </Checkbox>
            <Button onClick={props.onLeaveDraft} icon={<LogoutOutlined aria-hidden={true} />}>
                {Language.get("nc_mass_change_leave_draft")}
            </Button>
            <Button onClick={handleDelete} icon={<DeleteOutlined aria-hidden={true} />}>
                {Language.get("nc_mass_change_delete_draft")}
            </Button>
        </>
    );

    return (
        <div
            className="tw:bg-te-colorPrimaryBgHover"
            style={{
                ...props.style,
                display: "flex",
                alignItems: "center",
                paddingLeft: "16px",
                paddingRight: "16px",
                borderBottom: `solid 1px ${token.token.colorPrimaryHover}`,
            }}
        >
            <Typography.Title level={4} style={{ margin: "0", textTransform: "none" }}>
                {props.title}
            </Typography.Title>
            {
                <Button
                    disabled={props.isWorking}
                    style={{
                        marginLeft: "8px",
                    }}
                    icon={<EditOutlined aria-hidden={true} />}
                    onClick={() => {
                        setRenameModalOpen(true);
                    }}
                ></Button>
            }
            <div
                style={{
                    display: "flex",
                    flexDirection: "row-reverse",
                    alignItems: "center",
                    gap: "8px",
                    flexGrow: "1",
                }}
            >
                {loadIndicator || buttons}
            </div>
            <NamePromptModal
                open={renameModalOpen}
                title={Language.get("cal_header_popup_period_change_name")}
                onSaveName={handleRename}
                oldName={activeLayerData?.name ?? ""}
                onCancel={() => {
                    setRenameModalOpen(false);
                }}
            />
            {confirmModalContextHolder}
        </div>
    );
};

export default TeuiMassChangeBar;
